<template>
  <div class="h-dvh flex flex-col !bg-pitch-black text-total-white">
    <!-- Top Navbar -->
    <TopNavbar />
    <div class="overflow-y-scroll pb-2">
      <!-- Header -->
      <header class="flex justify-between items-center px-4 py-4">
        <!-- Back Button -->
        <div @click="goBack" class="flex items-center gap-2">
          <button>
            <img class="w-6 h-6" src="@/assets/images/left-chevron.svg" alt="Back" />
          </button>
          <h1 v-if="!route.query.from" class="text-prime-golden text-xl">All Agents</h1>
        </div>
        <!-- Edit & Delete Buttons -->
        <div class="flex gap-4 items-center">
          <button
            @click="editAgent"
            class="flex items-center gap-1 text-prime-golden text-sm"
          >
            <img src="@/assets/images/edit-pen.svg" alt="Edit" class="w-4 h-4" />
            <span class="underline underline-offset-4">edit</span>
          </button>
        </div>
      </header>
      <!-- Agent Title & Last Updated -->
      <div class="text-center my-6" :class="{ 'opacity-50': isDeleting }">
        <div class="grid grid-cols-[1fr_2fr_1fr] px-4 items-center">
          <div></div>
          <div class="text-center">
            <h2 class="text-2xl font-bold">{{ agent?.agent_name }}</h2>
            <!-- Default marker if applicable -->
            <span
              v-if="agent?.is_default"
              class="inline-block text-sm font-semibold whitespace-nowrap text-prime-golden bg-dark-grey rounded-md px-2 py-1 mt-2"
            >
              default
            </span>
          </div>
          <button @click="deleteAgent" class="text-red-500 flex justify-end">
            <img src="@/assets/images/trash-icon.svg" alt="Delete" class="w-5 h-5" />
          </button>
        </div>
        <p class="text-sm text-gray-400 mt-2">
          last updated at {{ parseDate(agent?.date_created) }}
        </p>
      </div>

      <!-- Provider Agent ID -->
      <div class="px-4 mb-8 mt-2" :class="{ 'opacity-50': isDeleting }">
        <h3 class="text-lg text-ash-grey flex font-semibold text-left">
          Provider Agent ID: {{ agent?.provider_agent_id }} ({{ agent?.provider_name }})
        </h3>
      </div>

      <!-- Model Name (new field) -->
      <div
        class="px-4 mb-8 mt-2"
        :class="{ 'opacity-50': isDeleting }"
        v-if="agent?.model_name"
      >
        <h3 class="text-lg text-ash-grey flex font-semibold text-left">
          Model Name: {{ agent?.model_name }}
        </h3>
      </div>

      <!-- Top_p, Temperature and Reasoning Effort Fields -->
      <div
        v-if="
          agent?.top_p !== null ||
          agent?.temperature !== null ||
          agent?.reasoning_effort !== null
        "
        class="px-4 flex flex-col space-y-4"
        :class="{ 'opacity-50': isDeleting }"
      >
        <div v-if="agent?.top_p !== null" class="flex flex-col items-start mb-4">
          <h4 class="text-lg font-semibold">Top_p</h4>
          <p class="text-md mt-1">{{ agent?.top_p }}</p>
        </div>
        <div v-if="agent?.temperature !== null" class="flex flex-col items-start mb-4">
          <h4 class="text-lg font-semibold">Temperature</h4>
          <p class="text-md mt-1">{{ agent?.temperature }}</p>
        </div>
        <div
          v-if="agent?.reasoning_effort !== null"
          class="flex flex-col items-start mb-4"
        >
          <h4 class="text-lg font-semibold">Reasoning Effort</h4>
          <p class="text-md mt-1">{{ agent?.reasoning_effort }}</p>
        </div>
      </div>

      <!-- Related Projects -->
      <div class="px-4 mt-8" :class="{ 'opacity-50': isDeleting }">
        <h4 class="text-lg flex font-semibold">Related Projects</h4>
        <ul class="list-none mt-2 space-y-2">
          <li v-for="project in agent?.projects" :key="project.id">
            <a
              :href="project.link"
              @click="goToProject(project.id)"
              class="flex items-center gap-2 underline text-prime-golden"
            >
              <span class="text-lg">{{ project.address_1 }}</span>
            </a>
          </li>
        </ul>
        <div v-if="agent?.projects && agent.projects.length === 0">
          <p class="text-left text-total-white-800">No related projects found.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import TopNavbar from "@/components/TopNavbar.vue";
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAgentStore } from "@/stores/agent";
import { parseDate } from "@/misc/util";

const router = useRouter();
const route = useRoute();
const agentStore = useAgentStore();

const agentId = route.params.id;
const agent = ref(null);
const isDeleting = ref(false);

onMounted(async () => {
  agent.value = await agentStore.fetchAgent(agentId);
  console.log(agent.value);
});

// Navigation actions
const goBack = () => {
  if (route.query.from && route.query.from[0] == "activation") {
    console.log("From activation: ", route.query.from[1]);
    router.push({ name: "ViewActivationPage", params: { id: route.query.from[1] } });
  } else {
    router.push({ name: "AgentsPage" });
  }
};

const editAgent = () => {
  router.push({ name: "EditAgentPage" });
};

const goToProject = (id) => {
  router.push({
    name: "ProjectPage",
    params: { id: id },
    query: { from: ["agent", agentId] },
  });
};

const deleteAgent = async () => {
  isDeleting.value = true;
  await agentStore.deleteAgent(agentId);
  router.push({ name: "AgentsPage" });
  isDeleting.value = false;
};
</script>

<style scoped></style>
